import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  Link,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AssessmentIcon from '@mui/icons-material/Assessment';
import axios from 'axios';
import ProjectForm from '../components/CreateProjectForm';
import FeasibilityAnalysisForm from '../components/FeasibilityAnalysisForm';
import api from '../services/api';
import WorkControls from '../components/WorkControls';
import WorkTimeline from '../components/WorkTimeline';

// TabPanel component for showing different tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feasibilityAnalysis, setFeasibilityAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [error, setError] = useState(null);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openAnalysisForm, setOpenAnalysisForm] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTechnical, setIsTechnical] = useState(false);
  const [tabValue, setTabValue] = useState(0); // For tabs
  const [workRecords, setWorkRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return false;
    }
    return token;
  };

  const checkUserRole = async () => {
    try {
      const response = await api.get('/users/me');
      if (response.data) {
        // Verificar se é admin ou técnico
        setIsAdmin(response.data.role_id === 1);
        setIsTechnical(response.data.role_id === 3);
      }
    } catch (error) {
      console.error('Erro ao verificar permissões do usuário:', error);
    }
  };

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const token = checkAuth();
      if (!token) return;

      const response = await api.get(`/projects/${id}`);
      
      setProject(response.data);
    } catch (error) {
      console.error('Erro ao buscar detalhes do projeto:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setError('Não foi possível carregar os detalhes do projeto');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeasibilityAnalysis = async () => {
    try {
      setLoadingAnalysis(true);
      const token = checkAuth();
      if (!token) return;

      const response = await api.get(`/feasibility-analysis/${id}`);
      setFeasibilityAnalysis(response.data);
    } catch (error) {
      // Não exibimos erro aqui, pois pode ser que o projeto ainda não tenha análise
      console.log('Análise de viabilidade não encontrada:', error);
      setFeasibilityAnalysis(null);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const fetchWorkRecords = async () => {
    try {
      setLoadingRecords(true);
      const response = await api.get(`/work-records/project/${id}`);
      setWorkRecords(response.data);
    } catch (error) {
      console.error('Erro ao buscar registros de trabalho:', error);
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchProjectDetails();
    checkUserRole();
    if (id) {
      fetchFeasibilityAnalysis();
    }
    if (id) {
      fetchWorkRecords();
    }
  }, [id, navigate]);

  const handleEditProject = async (formData) => {
    try {
      const token = checkAuth();
      if (!token) return;

      // Format data for API
      const updatedProject = {
        project_name: formData.name,
        description: formData.description,
        priority: formData.priority,
        responsible: formData.responsible,
        start_date: formData.startDate,
        end_date: formData.endDate
      };

      await api.put(`/projects/${id}`, updatedProject);

      setOpenEditForm(false);
      setNotification({
        open: true,
        message: 'Projeto atualizado com sucesso!',
        severity: 'success'
      });

      // Refresh project details
      fetchProjectDetails();
    } catch (error) {
      console.error('Erro ao atualizar projeto:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      setNotification({
        open: true,
        message: 'Erro ao atualizar o projeto: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    }
  };

  const handleSubmitAnalysis = async (analysisData) => {
    try {
      // Determine if we should update or create
      let response;
      if (feasibilityAnalysis) {
        // Update existing analysis
        response = await api.put(`/feasibility-analysis/${id}`, analysisData);
      } else {
        // Create new analysis
        response = await api.post('/feasibility-analysis/', analysisData);
      }

      setOpenAnalysisForm(false);
      setNotification({
        open: true,
        message: feasibilityAnalysis 
          ? 'Análise de viabilidade atualizada com sucesso!' 
          : 'Análise de viabilidade criada com sucesso!',
        severity: 'success'
      });

      // Refresh analysis
      fetchFeasibilityAnalysis();
    } catch (error) {
      console.error('Erro ao salvar análise de viabilidade:', error);
      setNotification({
        open: true,
        message: 'Erro ao salvar análise de viabilidade: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'alta':
        return 'error';
      case 'média':
        return 'warning';
      case 'baixa':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não definida';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleBack = () => {
    navigate('/projects');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleResponsibleClick = async () => {
    if (project.responsible) {
      try {
        // Buscar o ID do responsável com base no nome
        const response = await api.get(`/technical-responsibles/by-name/${project.responsible}`);
        if (response.data && response.data.responsible_id) {
          navigate(`/responsibles/${response.data.responsible_id}`);
        } else {
          // Caso não encontre o responsável específico, vai para a lista de responsáveis
          navigate('/responsibles');
        }
      } catch (error) {
        console.error('Erro ao buscar responsável:', error);
        // Se ocorrer algum erro, navega para a lista de responsáveis
        navigate('/responsibles');
      }
    }
  };

  const handleCreateAnalysis = () => {
    setOpenAnalysisForm(true);
  };

  const handleCompleteProject = async () => {
    try {
      const token = checkAuth();
      if (!token) return;

      // Confirmar com o usuário antes de concluir o projeto
      if (!window.confirm('Tem certeza que deseja concluir este projeto? Ele será marcado como finalizado e não aparecerá mais na lista de projetos ativos.')) {
        return;
      }

      // Atualizar o status do projeto para concluído (status_id = 2)
      // Apenas enviar os campos necessários para a API
      const updatedProject = {
        project_name: project.project_name,
        description: project.description,
        start_date: project.start_date,
        end_date: project.end_date,
        priority: project.priority,
        responsible: project.responsible,
        priority_order: project.priority_order,
        status_id: 2 // 2 representa o status "Concluído"
      };
      
      console.log('Enviando dados para atualização:', updatedProject);
      
      // Usar a API para atualizar o projeto
      const response = await api.put(`/projects/${id}`, updatedProject);
      console.log('Resposta da API:', response.data);

      setNotification({
        open: true,
        message: 'Projeto concluído com sucesso!',
        severity: 'success'
      });

      // Recarregar detalhes do projeto
      fetchProjectDetails();
    } catch (error) {
      console.error('Erro ao concluir o projeto:', error);
      setNotification({
        open: true,
        message: 'Erro ao concluir o projeto: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    }
  };

  const renderWorkSection = () => {
    return (
      <Box sx={{ mt: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: '8px',
            backgroundColor: theme => theme.palette.mode === 'dark' 
              ? 'rgba(144, 202, 249, 0.08)' 
              : 'rgba(25, 118, 210, 0.08)'
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3
            }}
          >
            Controle de Trabalho
          </Typography>
          
          {(isAdmin || isTechnical) && (
            <Box sx={{ mb: 3 }}>
              <WorkControls 
                projectId={id} 
                onWorkUpdate={fetchWorkRecords}
              />
            </Box>
          )}
        </Paper>

        <Box sx={{ mt: 4 }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 3,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              pb: 1,
              display: 'inline-block'
            }}
          >
            Histórico de Trabalho
          </Typography>
          {loadingRecords ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : workRecords.length === 0 ? (
            <Paper
              elevation={1}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: '8px'
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Nenhum registro de trabalho encontrado para este projeto.
              </Typography>
            </Paper>
          ) : (
            <WorkTimeline records={workRecords} />
          )}
        </Box>
      </Box>
    );
  };

  const renderFeasibilityAnalysis = () => {
    if (loadingAnalysis) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      );
    }

    if (!feasibilityAnalysis) {
      return (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: '8px',
            mt: 2
          }}
        >
          <Typography variant="body1" sx={{ mb: 3 }}>
            Este projeto ainda não possui análise de viabilidade.
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              onClick={handleCreateAnalysis}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              Criar Análise de Viabilidade
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography 
            variant="h5"
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              pb: 0.5,
              display: 'inline-block'
            }}
          >
            Análise de Viabilidade
          </Typography>
          {isAdmin && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleCreateAnalysis}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              Editar Análise
            </Button>
          )}
        </Box>

        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: 4
            } 
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            Setor Solicitante/Beneficiado
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Nome do setor/departamento:</Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.requesting_department}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Responsável pela solicitação:</Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.requester_name}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: 4
            } 
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            Escopo do Projeto
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Descrição do problema a ser resolvido:</Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.problem_description}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Objetivos do projeto:</Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.project_objectives}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: 4
            } 
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            Viabilidade Técnica
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Tecnicamente viável?</Typography>
              <Chip 
                label={feasibilityAnalysis.is_technically_feasible ? 'Sim' : 'Não'}
                color={feasibilityAnalysis.is_technically_feasible ? 'success' : 'error'}
                variant="outlined"
                sx={{ mt: 1, fontWeight: 500 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Requer integrações?</Typography>
              <Chip 
                label={feasibilityAnalysis.requires_integrations ? 'Sim' : 'Não'}
                color={feasibilityAnalysis.requires_integrations ? 'warning' : 'success'}
                variant="outlined"
                sx={{ mt: 1, fontWeight: 500 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Tecnologias utilizadas:</Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.technologies_used}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Limitações técnicas:</Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.technical_limitations || 'Nenhuma limitação identificada'}
              </Typography>
            </Grid>
            {feasibilityAnalysis.requires_integrations && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Sistemas para integração:</Typography>
                <Typography 
                  variant="body1"
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '1.05rem'
                  }}
                >
                  {feasibilityAnalysis.integration_systems}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper 
          elevation={2} 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 3,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            backgroundColor: theme => 
              feasibilityAnalysis.decision === 'Aprovado' 
                ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)') 
                : feasibilityAnalysis.decision === 'Rejeitado'
                  ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)') 
                  : (theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)'),
            '&:hover': {
              boxShadow: 4
            } 
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main'
            }}
          >
            Resultado da Análise
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Decisão:</Typography>
              <Chip 
                label={feasibilityAnalysis.decision} 
                color={
                  feasibilityAnalysis.decision === 'Aprovado' ? 'success' : 
                  feasibilityAnalysis.decision === 'Rejeitado' ? 'error' : 'warning'
                }
                sx={{ 
                  mt: 1, 
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2 
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Prioridade:</Typography>
              <Chip 
                label={feasibilityAnalysis.priority} 
                color={getPriorityColor(feasibilityAnalysis.priority)}
                sx={{ 
                  mt: 1, 
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Justificativa:</Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '1.05rem'
                }}
              >
                {feasibilityAnalysis.decision_justification}
              </Typography>
            </Grid>
            
            {feasibilityAnalysis.responsible_persons && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Pessoas Responsáveis:</Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {feasibilityAnalysis.responsible_persons.split(',').map((name, index) => (
                    <Chip 
                      key={index}
                      label={name.trim()} 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setTabValue(0)}
            sx={{ 
              mt: 2,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                boxShadow: 3
              }
            }}
          >
            Ver detalhes completos
          </Button>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !project) {
    return (
      <Container maxWidth="md">
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mb: 3 }}
        >
          Voltar para Projetos
        </Button>
        <Alert severity="error">{error || 'Projeto não encontrado'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          variant="outlined"
          sx={{ 
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              boxShadow: 3
            }
          }}
        >
          Voltar para Projetos
        </Button>
        
        <Box>
          {isAdmin && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setOpenEditForm(true)}
                sx={{ 
                  mr: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    boxShadow: 3
                  }
                }}
              >
                Editar Projeto
              </Button>

              {!feasibilityAnalysis && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssessmentIcon />}
                  onClick={handleCreateAnalysis}
                  sx={{ 
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                >
                  Análise de Viabilidade
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: theme => theme.shadows[3]
        }}
      >
        <Box 
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 3
          }}
        >
          <Box sx={{ mb: { xs: 2, sm: 0 } }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                background: theme => theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #90caf9 30%, #64b5f6 90%)'
                  : 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              {project.project_name}
            </Typography>
            <Chip 
              label={`#${project.project_id}`}
              size="small"
              variant="outlined"
              sx={{ mr: 1 }}
            />
          </Box>
          <Chip 
            label={`Prioridade: ${project.priority || 'Não definida'}`} 
            color={getPriorityColor(project.priority)}
            variant="outlined"
            size="medium"
            sx={{ 
              px: 1,
              borderWidth: 2,
              fontWeight: 500
            }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="project tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                minWidth: 'auto',
                px: 3
              }
            }}
          >
            <Tab label="Detalhes do Projeto" id="tab-0" />
            <Tab label="Controle de Trabalho" id="tab-1" />
            <Tab label="Análise de Viabilidade" id="tab-2" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  backgroundColor: theme => theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.08)' 
                    : 'rgba(25, 118, 210, 0.08)',
                  mb: 3
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  Descrição
                </Typography>
                <Typography variant="body1" paragraph>
                  {project.description || 'Sem descrição'}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  height: '100%'
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  Responsável
                </Typography>
                <Typography variant="body1">
                  {project.responsible ? (
                    <Link
                      component="button"
                      variant="body1"
                      onClick={handleResponsibleClick}
                      sx={{ 
                        textDecoration: 'none',
                        fontWeight: 500,
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {project.responsible}
                    </Link>
                  ) : (
                    'Não atribuído'
                  )}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  height: '100%'
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={project.status_id === 1 ? 'Em andamento' : 
                           project.status_id === 2 ? 'Concluído' : 
                           project.status_id === 3 ? 'Cancelado' : 'Desconhecido'}
                    color={project.status_id === 1 ? 'primary' : 
                           project.status_id === 2 ? 'success' : 
                           project.status_id === 3 ? 'error' : 'default'}
                    variant="outlined"
                    size="medium"
                  />
                  
                  {/* Botão para concluir projeto - apenas para técnicos e apenas se não estiver concluído */}
                  {isTechnical && project.status_id === 1 && (
                    <Tooltip title="Apenas técnicos podem concluir projetos">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleCompleteProject}
                        sx={{ 
                          ml: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          borderRadius: '8px'
                        }}
                      >
                        Concluir Projeto
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  height: '100%'
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  Data de Início
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 500
                  }}
                >
                  {formatDate(project.start_date)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: '8px',
                  height: '100%'
                }}
              >
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  Data de Término
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 500
                  }}
                >
                  {formatDate(project.end_date)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  borderRadius: '8px',
                  backgroundColor: theme => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.03)'
                }}
              >
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                  Criado em: {new Date(project.created_at).toLocaleString()}
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                  Última atualização: {new Date(project.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderWorkSection()}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {renderFeasibilityAnalysis()}
        </TabPanel>
      </Paper>

      {/* Formulário de edição */}
      <ProjectForm
        open={openEditForm}
        onClose={() => setOpenEditForm(false)}
        onSubmit={handleEditProject}
        initialData={project}
        isEditing={true}
      />

      {/* Formulário de análise de viabilidade */}
      <FeasibilityAnalysisForm
        open={openAnalysisForm}
        onClose={() => setOpenAnalysisForm(false)}
        onSubmit={handleSubmitAnalysis}
        initialData={feasibilityAnalysis}
        projectId={parseInt(id)}
      />

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProjectDetails; 